import { useEffect, useMemo, useState } from "react"
import { MaterialReactTable } from "material-react-table"
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  styled,
  Typography,
} from "@mui/material"
import MixedChart from "../../Chart/MixedChart"
import { TableHeading, TableWrapperPaper } from "./DashboardTables"
import { colors } from "../../../../styles/commonStyles"
import { useSelector } from "react-redux"
import { useResponseHandle } from "../../../hooks"
import DashboardAPI from "./../../../../api/dashboard"
import { getTokenLocal, isSubArraySubsetOf } from "../../../../helper/utils"
import { useNavigate } from "react-router-dom"
import AddIcon from "@mui/icons-material/Add"
import { useAppDispatch } from "../../../redux/hooks"
import { setTopMetricsSelections } from "../../../redux/slices/dashboardSlice"

export type User = {
  metricName: string
  mtdBudget: number
  mtdActual: number
  variance: number
}

interface datasetPropType {
  chartName: string
  labels: any[]
  datasets: {
    label: string
    data_type: string
    type: string
    data: any[]
  }[]
}

interface scaleType {
  isMultiAxis?: boolean
  axes: {
    id: string
    type?: string
    position?: string
    axisUnit?: string
    offset?: boolean
    stackWeight?: number
    [key: string]: any
    label?: {
      display?: boolean
      text?: string
      position?: string
      color?: string
      align?: string
    }
  }[]
}

export const ChartWrapper = styled(Box)({
  margin: "1%",
  padding: "0px 15px",
  backgroundColor: "#fff",
  borderRadius: "4px",
  boxShadow: "0px 2px 8px #00000022",
})

interface chartPlottingDataType {
  chartName: string | undefined
  data: {
    labels: string[]
    datasets: {
      label: string
      data: number[]
      backgroundColor?: string | undefined
      borderWidth?: number | undefined
      borderColor?: string | undefined
      type?: any
      yAxisID?: string | undefined
    }[]
  }
  scales:
    | {
        isMultiAxis?: boolean | undefined
        axes?:
          | {
              [key: string]: any
              id: string
              type?: string | undefined
              position?: string | undefined
              axisUnit?: string | undefined
              offset?: boolean | undefined
              stackWeight?: number | undefined
              label?:
                | {
                    display?: boolean | undefined
                    text?: string | undefined
                    position?: string | undefined
                    color?: string | undefined
                    align?: string | undefined
                  }
                | undefined
            }[]
          | undefined
      }
    | undefined
}

interface Dataset {
  scales?: {}
  chartName?: string
  data: {
    labels: string[]
    datasets: {
      label: string
      data: number[]
      backgroundColor?: string
      borderWidth?: number
      borderColor?: string
      type?: string
      yAxisID?: string
    }[]
  }
}

const dashboardApi = new DashboardAPI()
export const OverviewTable = ({ loading }: { loading?: boolean }) => {
  const token = getTokenLocal()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [topMetrics, setTopMetrics] = useState<any>([])
  const [selectedMetrics, setSelectedMetrics] = useState<any>([])

  const [topMetricsChartData, setTopMetricsChartData] = useState<any>([])
  const [topMetricsChartPlottingData, setTopMetricsChartPlottingData] =
    useState<chartPlottingDataType[]>([])

  const [metricChanged, setMetricChanged] = useState(false)

  const [open, setOpen] = useState(false)

  const overviewGraphs = useSelector(
    (state: any) => state.dashboard?.overviewGraphData,
  )
  const maevaSelectedGroupsNoAll = useSelector((state: any) => {
    return state.metric.maevaSelectedGroupsNoAll
  })

  const maevaSelectedPractices = useSelector((state: any) => {
    return state.metric.maevaSelectedPractices
  })

  const maevaSelectedRegion = useSelector((state: any) => {
    return state.metric.maevaSelectedRegions?.[0]
  })

  const maevaSelectedRegions = useSelector((state: any) => {
    return state.metric.maevaSelectedRegions
  })

  const DailyProductionGoalData = useSelector((state: any) => {
    return state?.dashboard?.overviewData?.dailyProductionGoal
  })

  const dailyCollectionGoalTableData = useSelector((state: any) => {
    return state?.dashboard?.overviewData?.dailyCollectionGoal
  })
  const yesterdayData = useSelector((state: any) => {
    return state?.dashboard?.overviewData?.yesterday
  })

  const topMetricsSelections = useSelector((state: any) => {
    return state?.dashboard?.topMetricsSelections
  })

  const columns = useMemo<any[]>(
    () => [
      {
        accessorKey: "metric_name",
        header: "Metric Name",
        enableSorting: false,
        Cell: ({ row }: any) => <Box>{row.original?.metric_name}</Box>,

        muiTableBodyCellProps: () => ({
          sx: {
            fontWeight: "400",
            border: "0.5px solid #E4E4E4",
          },
        }),
      },
      {
        accessorKey: "mtd_budget",
        header: "MTD Budget",
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Box>${parseInt(row.original?.mtd_budget || 0).toLocaleString()}</Box>
        ),
        muiTableBodyCellProps: () => ({
          sx: {
            border: "0.5px solid #E4E4E4",
          },
        }),
      },
      {
        accessorKey: "mtd_actual",
        header: "MTD Actual",
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Box>${parseInt(row.original?.mtd_actual || 0).toLocaleString()}</Box>
        ),
        muiTableBodyCellProps: () => ({
          sx: {
            fontWeight: "400",
            border: "0.5px solid #E4E4E4",
          },
        }),
      },
      {
        accessorKey: "mtd_variance",
        header: "Variance",
        enableSorting: false,
        size: 200,
        Cell: ({ renderedCellValue }: any) => (
          <Box>
            {renderedCellValue
              ? `$${parseInt(renderedCellValue || 0).toLocaleString()}`
              : "-"}
          </Box>
        ),
        muiTableBodyCellProps: ({ cell }: any) => ({
          sx: {
            // padding: "0px 2%",
            "& div": {
              display: "flex",
              whiteSpace: "wrap",
              margin: "auto",
              justifyContent: "center",
              padding: "5%",

              border:
                cell.getValue() > 0
                  ? "1px solid #FFFFFF"
                  : cell.getValue() < 0
                  ? "1px solid #FFFFFF"
                  : null,
              backgroundColor:
                cell.getValue() > 0
                  ? colors.primaryGreen
                  : cell.getValue() < 0
                  ? colors.primaryRed
                  : "#FFFFFF",
              color:
                cell.getValue() == 0
                  ? colors.primaryBlack
                  : colors.primaryWhite,
            },
          },
        }),
      },
      {
        accessorKey: "month_end_goal",
        header: "Month End Goal",
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Box>
            ${parseInt(row.original?.month_end_goal || 0).toLocaleString()}
          </Box>
        ),
        muiTableBodyCellProps: () => ({
          sx: {
            fontWeight: "400",
            border: "0.5px solid #E4E4E4",
          },
        }),
      },
      {
        accessorKey: "projected_amount",
        header: "Projected Amount",
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Box>
            ${parseInt(row.original?.projected_amount || 0).toLocaleString()}
          </Box>
        ),
        muiTableBodyCellProps: () => ({
          sx: {
            fontWeight: "400",
            border: "0.5px solid #E4E4E4",
          },
        }),
      },
      {
        accessorKey: "projected_variance",
        header: "Projected Variance",
        enableSorting: false,
        Cell: ({ renderedCellValue }: any) => (
          <Box>
            {renderedCellValue
              ? `$${parseInt(renderedCellValue || 0).toLocaleString()}`
              : "-"}
          </Box>
        ),
        muiTableBodyCellProps: ({ cell }: any) => ({
          sx: {
            padding: "0px 2%",
            "& div": {
              display: "flex",
              whiteSpace: "wrap",
              margin: "auto",
              justifyContent: "center",
              padding: "5%",

              border:
                cell.getValue() > 0
                  ? "1px solid #FFFFFF"
                  : cell.getValue() < 0
                  ? "1px solid #FFFFFF"
                  : null,
              backgroundColor:
                cell.getValue() > 0
                  ? colors.primaryGreen
                  : cell.getValue() < 0
                  ? colors.primaryRed
                  : "#FFFFFF",
              color:
                cell.getValue() == 0
                  ? colors.primaryBlack
                  : colors.primaryWhite,
            },
          },
        }),
      },
      {
        accessorKey: "adjusted_daily_goal",
        header: "Adjusted Daily Goal",
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Box>
            ${parseInt(row.original?.adjusted_daily_goal || 0).toLocaleString()}
          </Box>
        ),
        muiTableBodyCellProps: () => ({
          sx: {
            fontWeight: "400",
            border: "0.5px solid #E4E4E4",
          },
        }),
      },
    ],
    [],
  )
  const dailyCollectionGoalColumns = useMemo<any[]>(
    () => [
      {
        accessorKey: "metric_name",
        header: "Metric Name",
        enableSorting: false,
        Cell: ({ row }: any) => <Box>{row.original?.metric_name}</Box>,

        muiTableBodyCellProps: () => ({
          sx: {
            fontWeight: "400",
            border: "0.5px solid #E4E4E4",
          },
        }),
      },
      {
        accessorKey: "ytd_budget",
        header: "YTD Budget",
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Box>${parseInt(row.original?.ytd_budget || 0).toLocaleString()}</Box>
        ),
        muiTableBodyCellProps: () => ({
          sx: {
            fontWeight: "400",
            border: "0.5px solid #E4E4E4",
          },
        }),
      },
      {
        accessorKey: "ytd_actual",
        header: "YTD Actual",
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Box>
            $ {parseInt(row.original?.ytd_actual || 0).toLocaleString()}
          </Box>
        ),
        muiTableBodyCellProps: () => ({
          sx: {
            border: "0.5px solid #E4E4E4",
          },
        }),
      },
      {
        accessorKey: "ytd_variance",
        header: "Variance",
        enableSorting: false,
        Cell: ({ renderedCellValue }: any) => (
          <Box>
            {renderedCellValue
              ? `$${parseInt(renderedCellValue || 0).toLocaleString()}`
              : "-"}
          </Box>
        ),
        muiTableBodyCellProps: ({ cell }: any) => ({
          sx: {
            // padding: "0 2.5%",
            // padding: "0",
            "& div": {
              display: "flex",
              margin: "auto",
              whiteSpace: "wrap",
              justifyContent: "center",
              padding: "5px 2px",

              border:
                cell.getValue() > 0
                  ? "1px solid #FFFFFF"
                  : cell.getValue() < 0
                  ? "1px solid #FFFFFF"
                  : null,
              backgroundColor:
                cell.getValue() > 0
                  ? colors.primaryGreen
                  : cell.getValue() < 0
                  ? colors.primaryRed
                  : "#FFFFFF",
              color:
                cell.getValue() == 0
                  ? colors.primaryBlack
                  : colors.primaryWhite,
            },
          },
        }),
      },
    ],
    [],
  )

  const yesterdayColumns = useMemo<any[]>(
    () => [
      {
        accessorKey: "-",
        header: "Metric Name",
        enableSorting: false,
        Cell: ({ row }: any) => <Box>Production</Box>,
        muiTableBodyCellProps: () => ({
          sx: {
            fontWeight: "400",
            border: "0.5px solid #E4E4E4",
          },
        }),
      },
      {
        accessorKey: "production_actual",
        header: "Actual",
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Box>
            ${parseInt(row.original?.production_actual || 0).toLocaleString()}
          </Box>
        ),
        muiTableBodyCellProps: () => ({
          sx: {
            border: "0.5px solid #E4E4E4",
          },
        }),
      },
      {
        accessorKey: "production_goal",
        header: "Goal",
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Box>
            ${parseInt(row.original?.production_goal || 0).toLocaleString()}
          </Box>
        ),
        muiTableBodyCellProps: () => ({
          sx: {
            fontWeight: "400",
            border: "0.5px solid #E4E4E4",
          },
        }),
      },
      {
        accessorKey: "production_variance",
        header: "Difference",
        enableSorting: false,
        Cell: ({ renderedCellValue }: any) => (
          <Box>
            {renderedCellValue
              ? `$${parseInt(renderedCellValue || 0).toLocaleString()}`
              : "-"}
          </Box>
        ),
        muiTableBodyCellProps: ({ cell }: any) => ({
          sx: {
            padding: "0px 2%",
            "& div": {
              display: "flex",
              whiteSpace: "wrap",
              margin: "auto",
              justifyContent: "center",
              padding: "5%",

              border:
                cell.getValue() > 0
                  ? "1px solid #FFFFFF"
                  : cell.getValue() < 0
                  ? "1px solid #FFFFFF"
                  : null,
              backgroundColor:
                cell.getValue() > 0
                  ? colors.primaryGreen
                  : cell.getValue() < 0
                  ? colors.primaryRed
                  : "#FFFFFF",
              color:
                cell.getValue() == 0
                  ? colors.primaryBlack
                  : colors.primaryWhite,
            },
          },
        }),
      },
      {
        accessorKey: "_",
        header: "Metric Name",
        enableSorting: false,
        Cell: ({ row }: any) => <Box>Collections(Payments)</Box>,
        muiTableBodyCellProps: () => ({
          sx: {
            fontWeight: "400",
            border: "0.5px solid #E4E4E4",
          },
        }),
      },
      {
        accessorKey: "collection_actual",
        header: "Actual",
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Box>
            ${parseInt(row.original?.collection_actual || 0).toLocaleString()}
          </Box>
        ),
        muiTableBodyCellProps: () => ({
          sx: {
            fontWeight: "400",
            border: "0.5px solid #E4E4E4",
          },
        }),
      },
      {
        accessorKey: "collection_goal",
        header: "Goal",
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Box>
            ${parseInt(row.original?.collection_goal || 0).toLocaleString()}
          </Box>
        ),
        muiTableBodyCellProps: () => ({
          sx: {
            fontWeight: "400",
            border: "0.5px solid #E4E4E4",
          },
        }),
      },
      {
        accessorKey: "collection_variance",
        header: "Difference",
        enableSorting: false,
        Cell: ({ renderedCellValue }: any) => (
          <Box>
            {renderedCellValue
              ? `$${parseInt(renderedCellValue || 0).toLocaleString()}`
              : "-"}
          </Box>
        ),
        muiTableBodyCellProps: ({ cell }: any) => ({
          sx: {
            padding: "0px 2%",
            "& div": {
              display: "flex",
              whiteSpace: "wrap",
              margin: "auto",
              justifyContent: "center",
              padding: "5%",

              border:
                cell.getValue() > 0
                  ? "1px solid #FFFFFF"
                  : cell.getValue() < 0
                  ? "1px solid #FFFFFF"
                  : null,
              backgroundColor:
                cell.getValue() > 0
                  ? colors.primaryGreen
                  : cell.getValue() < 0
                  ? colors.primaryRed
                  : "#FFFFFF",
              color:
                cell.getValue() == 0
                  ? colors.primaryBlack
                  : colors.primaryWhite,
            },
          },
        }),
      },
    ],
    [],
  )

  /*
   * Fetches the top metrics asynchronously and updates the state accordingly.
   */
  const fetchTopMetrics = async () => {
    const response = await dashboardApi.getTopMetrics(token)

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        // setting selected metrics based on value from the store, by checking if those values exists in the top metrics array
        topMetricsSelections?.length === 0
          ? setSelectedMetrics([])
          : isSubArraySubsetOf(result?.data, topMetricsSelections)
          ? setSelectedMetrics(topMetricsSelections)
          : (dispatch(setTopMetricsSelections([])), selectedMetrics([]))

        setTopMetrics(result?.data)
      }
    } else {
      navigate("/auth")
    }
  }

  useEffect(() => {
    fetchTopMetrics()
  }, [maevaSelectedRegions, maevaSelectedGroupsNoAll, maevaSelectedPractices])

  const chartBackgroundColors = [
    "#58B1C9",
    "#1A428A",
    "#BC426B",
    "#3366D5",
    "#EBAE07",
    "#A944C5",
    "#8BB147",
  ]

  const [chartPlottingData, setChartPlottingData] = useState<
    chartPlottingDataType[] | []
  >([])

  let colorIndex = 0
  const getChartColor = () => {
    if (colorIndex >= chartBackgroundColors.length) {
      colorIndex = 0
    }
    const color = chartBackgroundColors[colorIndex]
    colorIndex = colorIndex + 1
    return color
  }

  const dynamicDatasets = (charts: datasetPropType, noOfCharts: number) => {
    if (noOfCharts === 0) {
      return
    }

    const newDataset: Dataset = {
      scales: {},
      chartName: charts.chartName,
      data: {
        labels: [],
        datasets: [],
      },
    }

    newDataset.data.labels = charts.labels
    newDataset.chartName = charts.chartName

    const axes: any[] = []

    charts.datasets.forEach((data, i) => {
      const color = getChartColor()
      const datasetNew: any = {}
      datasetNew.label = data.label
      datasetNew.data = data.data
      datasetNew.backgroundColor = color
      datasetNew.borderColor = color
      datasetNew.type = data.type
      datasetNew.order = data.type === "line" ? 0 : 1
      datasetNew.yAxisID = data.data_type
      !axes.includes(data.data_type) && axes.unshift(data.data_type)

      newDataset.data.datasets.push(datasetNew)
    })

    const scale: scaleType = {
      isMultiAxis: false,
      axes: [],
    }

    if (axes.length > 1) {
      scale.isMultiAxis = true
    }

    axes.forEach((ax) => {
      const newAxis: {
        id: string
        type?: string
        position?: string
        axisUnit?: string
        offset?: boolean
        stackWeight?: number
        [key: string]: any
        label?: {
          display?: boolean
          text?: string
          position?: string
          color?: string
          align?: string
        }
      } = {
        id: "",
      }

      newAxis.id = ax
      newAxis.position = "left"
      newAxis.axisUnit = ax
      newAxis.label = newAxis.label || {}
      newAxis.label.display = ax === "%" ? true : false
      newAxis.offset = ax === "$" ? true : false
      // newAxis.label.text = ax === "%" ? "Month Actual" : ""
      newAxis.stackWeight = ax === "%" ? 1 : 2
      scale.axes.push(newAxis)
    })

    newDataset.scales = scale
    return newDataset
  }

  useEffect(() => {
    setChartPlottingData([])
    const chartsData: any[] = Object.values(overviewGraphs)
    chartsData.forEach((chart) => {
      setChartPlottingData((prev: any) => [
        ...prev,
        dynamicDatasets(chart, chartsData?.length),
      ])
    })
  }, [overviewGraphs])

  const handleGetTopMetricsSubmit = async () => {
    setTopMetricsChartPlottingData([])
    if (topMetricsSelections.length > 0) {
      const response = await dashboardApi.postTopMetricsCharts(
        token,
        maevaSelectedGroupsNoAll[0]?.group_sk,
        maevaSelectedRegion?.name || null,
        maevaSelectedPractices[0]?.practice_sk || null,
        topMetricsSelections.map((item: { metric_id: any }) => item.metric_id),
      )

      const result = useResponseHandle({
        apiResponse: response,
        successMessage: false,
      })

      if (result?.isAuthorized) {
        if (result?.statusCode == 200) {
          setTopMetricsChartData(result?.data)
        }
      }
    }
    setMetricChanged(false)
  }

  useEffect(() => {
    setSelectedMetrics(topMetricsSelections)
  }, [topMetricsSelections])

  useEffect(() => {
    handleGetTopMetricsSubmit()
  }, [maevaSelectedRegions, maevaSelectedGroupsNoAll, maevaSelectedPractices])

  useEffect(() => {
    metricChanged && handleGetTopMetricsSubmit()
  }, [metricChanged])

  const handleSelectChange = (e: any, child: any) => {
    if (child.props.children[1] === "Select All") {
      if (selectedMetrics.length === topMetrics.length) {
        setSelectedMetrics([])
      } else {
        setSelectedMetrics(topMetrics)
      }
    } else {
      setSelectedMetrics(e.target.value)
    }
  }

  useEffect(() => {
    setTopMetricsChartPlottingData([])
    const chartsData: any[] = Object.values(topMetricsChartData)
    chartsData.forEach((chart) => {
      setTopMetricsChartPlottingData((prev: any) => [
        ...prev,
        dynamicDatasets(chart, chartsData?.length),
      ])
    })
  }, [topMetricsChartData])

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            padding: "20px",
            flexDirection: "column",
            alignItems: "flex-start",
            // maxWidth: "100%",
            // overflow: "auto",
          }}
        >
          <TableWrapperPaper sx={{}}>
            <Box sx={{ display: "flex" }}>
              <Typography
                sx={{
                  pl: "25px",
                  py: "8px",
                  fontWeight: "bold",
                  flex: 1,
                }}
              >
                {`Daily Production Goal - ${
                  yesterdayData
                    ? parseInt(
                        yesterdayData[0]?.production_goal || 0,
                      ).toLocaleString()
                    : "N/A"
                }`}
              </Typography>
              <Typography
                sx={{
                  pl: "25px",
                  py: "8px",
                  fontWeight: "bold",
                  flex: 1,
                  borderLeft: "1px solid #ACAAAD",
                }}
              >
                {`Daily Collection Goal - ${
                  yesterdayData
                    ? parseInt(
                        yesterdayData[0]?.collection_goal || 0,
                      ).toLocaleString()
                    : "N/A"
                }`}
              </Typography>
            </Box>
            <MaterialReactTable
              columns={columns}
              data={DailyProductionGoalData || []}
              enableStickyHeader={true}
              enableColumnActions={false}
              enableColumnFilters={false}
              enablePagination={false}
              enableDensityToggle={false}
              enableSorting={true}
              enableBottomToolbar={false}
              enableTopToolbar={false}
              state={{ isLoading: loading }}
              muiTableHeadProps={{
                sx: {
                  border: "0.5px solid #FFFFFF",
                  boxShadow: "#00000022 1.95px 0px 2.6px",
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  backgroundColor: "#3366D6",
                  color: "#FFFFFF",
                  border: "0.5px solid #FFFFFF",
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  border: "0.5px solid #E4E4E4",
                  padding: 0,
                },
              }}
            />
          </TableWrapperPaper>
          <TableWrapperPaper
            sx={{
              minWidth: "340px",
              width: { sm: "100%", md: "70%", lg: "50%" },
              mt: "20px",
            }}
          >
            <MaterialReactTable
              columns={dailyCollectionGoalColumns}
              data={dailyCollectionGoalTableData || []}
              enableStickyHeader={true}
              enableColumnActions={false}
              enableColumnFilters={false}
              enablePagination={false}
              enableDensityToggle={false}
              enableSorting={true}
              enableBottomToolbar={false}
              enableTopToolbar={false}
              state={{ isLoading: loading }}
              muiTableHeadProps={{
                sx: {
                  border: "0.5px solid #FFFFFF",
                  boxShadow: "#00000022 1.95px 0px 2.6px",
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  backgroundColor: "#3366D6",
                  color: "#FFFFFF",
                  border: "0.5px solid #FFFFFF",
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  border: "0.5px solid #E4E4E4",
                  padding: 0,
                },
              }}
            />
          </TableWrapperPaper>
        </Box>
        <Box
          sx={{
            maxWidth: "100%",
            overflow: "auto",
            height: "fit-content",
          }}
        >
          <TableWrapperPaper
            sx={{
              m: "2% auto 2% auto",
            }}
          >
            <TableHeading>Yesterday/Prior Day</TableHeading>
            <MaterialReactTable
              columns={yesterdayColumns}
              data={yesterdayData || []}
              enableStickyHeader={true}
              enableColumnActions={false}
              enableColumnFilters={false}
              enablePagination={false}
              enableDensityToggle={false}
              enableSorting={true}
              enableBottomToolbar={false}
              enableTopToolbar={false}
              state={{ isLoading: loading }}
              muiTableHeadProps={{
                sx: {
                  border: "0.5px solid #FFFFFF",
                  boxShadow: "#00000022 1.95px 0px 2.6px",
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  backgroundColor: "#3366D6",
                  color: "#FFFFFF",
                  border: "0.5px solid #FFFFFF",
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  border: "0.5px solid #E4E4E4",
                  padding: 0,
                },
              }}
            />
          </TableWrapperPaper>
        </Box>
      </Box>
      <Grid container sx={{ m: "10px 0 20px 0", padding: "0 1%" }}>
        {Object.keys(chartPlottingData)?.length > 0 ? (
          chartPlottingData.map(
            (chart: chartPlottingDataType, index: number | undefined) => (
              <Grid item xs={12} sm={12} md={6} lg={6} key={index}>
                <ChartWrapper>
                  <MixedChart
                    chartName={chart.chartName}
                    chartData={chart.data}
                    scales={chart.scales}
                  />
                </ChartWrapper>
              </Grid>
            ),
          )
        ) : (
          <Box
            sx={{
              height: "100px",
              width: "100%",
              justifyContent: "center",
              display: "flex",
              gap: "1.5rem",
              fontStyle: "italic",
            }}
          >
            Plotting the graphs... <CircularProgress size={"1.5rem"} />
          </Box>
        )}
      </Grid>
      <Box
        sx={{
          m: "10px 0 20px 10px",
          padding: "0 1%",
          gap: "5rem",
        }}
      >
        <Box>
          <InputLabel
            id="demo-simple-select-label"
            sx={{ fontSize: "12px", fontWeight: "600", mb: "0.5rem" }}
          >
            Top Metrics
          </InputLabel>
          <Box
            sx={{
              // minHeight: "32px",
              maxWidth: "100%",
              // m: "2%",
              p: "0.5rem",
              backgroundColor: "#fff",
              display: "flex",
              flexWrap: "wrap",
              gap: "0.5rem",
              border: "1px solid #BEBEBE ",
              borderRadius: "4px",
            }}
          >
            <Button
              onClick={() => setOpen(true)}
              sx={{
                height: "32px",
              }}
              variant="text"
              startIcon={<AddIcon />}
            >
              Add Metrics
            </Button>
            <Select
              open={open}
              sx={{
                height: "0px",
                width: "0px",
                visibility: "hidden",
                fontSize: "14px",
                marginTop: "5px",
                backgroundColor: "#FFFFFF",
              }}
              MenuProps={{
                sx: {
                  // width: "fit-content",
                  maxHeight: "550px",
                  my: 1,
                },
              }}
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              multiple
              value={selectedMetrics}
              onChange={(e, child) => {
                handleSelectChange(e, child)
              }}
              onClose={() => {
                setSelectedMetrics(topMetricsSelections)
                setOpen(false)
              }}
            >
              <MenuItem
                sx={{ width: "100%", gap: "10px", padding: "10px 10px" }}
                value={"All"}
              >
                <Checkbox
                  name="select-checkbox"
                  checked={selectedMetrics.length === topMetrics.length}
                  sx={{ p: 0 }}
                />
                Select All
              </MenuItem>
              {topMetrics.map((item: any) => (
                <MenuItem
                  key={item.metric_id}
                  value={item}
                  sx={{
                    width: "100%",
                    gap: "10px",
                    padding: "10px 10px",
                    p: 0,
                  }}
                >
                  <Checkbox checked={selectedMetrics.indexOf(item) > -1} />
                  <ListItemText primary={item.metric_name} />
                </MenuItem>
              ))}

              <Box
                sx={{
                  position: "sticky",
                  bottom: "0px",
                  minHeight: "50px",
                  backgroundColor: "#fff",
                  marginBottom: -1,
                  pb: "8px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  fullWidth
                  disableFocusRipple
                  key={"apply-close"}
                  variant="contained"
                  onClick={() => {
                    dispatch(setTopMetricsSelections(selectedMetrics))
                    setMetricChanged(true)
                    setOpen(false)
                  }}
                  sx={{
                    height: "40px",
                    width: "180px",
                    textTransform: "none",
                    fontWeight: "bold",
                    justifyContent: "center",

                    mt: "5px",
                    borderWidth: 0,
                    backgroundColor: colors.primaryBlue,
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Select>

            {topMetricsSelections.length > 0 &&
              topMetricsSelections.map((item: any) => (
                // item?.selected ? (
                <Chip
                  sx={{
                    height: "32px",
                    gap: "0.5rem",
                    borderRadius: "4px",
                    py: "0.8rem",
                  }}
                  key={item.metric_id}
                  size="small"
                  color="primary"
                  variant="filled"
                  label={item.metric_name}
                  onDelete={() => {
                    dispatch(
                      setTopMetricsSelections(
                        selectedMetrics.filter(
                          (metric: any) => metric.metric_id !== item.metric_id,
                        ),
                      ),
                    )

                    setMetricChanged(true)
                  }}
                />
              ))}
          </Box>
        </Box>
      </Box>
      <Grid container>
        {Object.keys(topMetricsChartPlottingData)?.length > 0 ? (
          topMetricsChartPlottingData.map(
            (chart: chartPlottingDataType, index: number | undefined) => (
              <Grid item xs={12} sm={12} md={6} lg={6} key={index}>
                <ChartWrapper>
                  <MixedChart
                    chartName={chart.chartName}
                    chartData={chart.data}
                    scales={chart.scales}
                  />
                </ChartWrapper>
              </Grid>
            ),
          )
        ) : loading ? (
          <Box
            sx={{
              height: "100px",
              width: "100%",
              justifyContent: "center",
              display: "flex",
              gap: "1.5rem",
              fontStyle: "italic",
            }}
          >
            Plotting the graphs... <CircularProgress size={"1.5rem"} />
          </Box>
        ) : (
          <></>
        )}
      </Grid>
    </Box>
  )
}

export default OverviewTable
